import { useContext, useEffect, useState } from 'react';

import { DefaultBrowserBehavior } from 'amazon-chime-sdk-js';
import { isOptionActive, useAudioOutputs, useLocalAudioOutput, useMeetingManager, useMeetingStatus } from 'amazon-chime-sdk-component-library-react';
import { Divider } from '@mui/material';

import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';

import ControlButton from './control-button';
import CustomPopover, { CustomPopoverItemProps } from './custom-popover';

interface AudioOutProps {
    startWithAudio: boolean;
    disabled?: boolean;
}

const DropDownAudioButton: React.FC<AudioOutProps> = ({
    startWithAudio,
    disabled = false,
}) => {

    const status = useMeetingStatus();

    const meetingManager = useMeetingManager();
    const { devices, selectedDevice } = useAudioOutputs();
    const { isAudioOn, toggleAudio } = useLocalAudioOutput();
    const [dropdownOptions, setDropdownOptions] = useState<CustomPopoverItemProps[]>([]);


    useEffect(() => {
        if (status != 1) {
            return;
        }

        if (isAudioOn !== startWithAudio) {
            toggleAudio();
        }
    }, [startWithAudio, status]);

    const onSelectedChanged = (device: CustomPopoverItemProps) => {
        setSelected(device.id);
    }

    const setSelected = async (deviceId: string): Promise<void> => {
        try {
            if (new DefaultBrowserBehavior().supportsSetSinkId()) {
                await meetingManager.startAudioOutputDevice(deviceId);
            } else {
                console.error(
                    'AudioOutputControl cannot select audio output device because browser does not support setSinkId operation.'
                );
            }
        } catch (error) {
            console.error('AudioOutputControl failed to select audio output device');
        }
    };

    useEffect(() => {
        const getDropdownOptions = async (): Promise<void> => {

            const deviceList: CustomPopoverItemProps[] = [];

            devices.map(async (device) => {
                const popoverItem: CustomPopoverItemProps = {
                    label: device.label,
                    id: device.deviceId,
                    active: await isOptionActive(device, device.deviceId)
                };

                deviceList.push(popoverItem);
            });

            setDropdownOptions(deviceList);
        };

        getDropdownOptions();
    }, [
        devices,
        selectedDevice,
        meetingManager,
        meetingManager.startAudioOutputDevice,
    ]);

    const sendMessageAndToggle = () => {
        toggleAudio();
    }

    return (
        <ControlButton
            disabled={disabled}
            activeIcon={<VolumeUpIcon color="action" />}
            inactiveIcon={<VolumeOffIcon color="action" />}
            active={isAudioOn}
            onClick={sendMessageAndToggle}
            activeLabel={"Disable Speaker"}
            inactiveLabel={"Enable Speaker"}
            showText={false}>
            <Divider orientation="vertical" />
            <CustomPopover
                options={dropdownOptions}
                openPopOverLabel="Select Audio"
                selectedOption={selectedDevice?.toString() || ""}
                setSelectedOption={onSelectedChanged}
            />
        </ControlButton >
    );
};

export default DropDownAudioButton;